import React, { useState, useEffect } from 'react';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { Package, CheckCircle, Tag, AlertTriangle, Copy, FileWarning } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { clientDashboardService } from '../../services/clientDashboard';

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
  refreshKey: number;
}

const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#06b6d4'];

const ClientDashboardMain: React.FC<Props> = ({ filters, refreshKey }) => {
  const [metrics, setMetrics] = useState<any>(null);
  const [charts, setCharts] = useState<any>(null);
  const [pallets, setPallets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const [metricsRes, chartsRes, palletsRes] = await Promise.all([
          clientDashboardService.getMetrics(filters),
          clientDashboardService.getCharts(filters),
          clientDashboardService.getPallets(filters)
        ]);
        setMetrics(metricsRes.data);
        setCharts(chartsRes.data);
        setPallets(palletsRes.data || []);
      } catch (err: any) {
        if (err.response?.status !== 401) {
          setError('Erro ao carregar dados do dashboard. Tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.periodo, filters.dataInicio, filters.dataFim, refreshKey]);

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="client-dashboard-spinner" />
        Carregando dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-dashboard-error-message">{error}</div>
    );
  }

  const cards = [
    { label: 'Paletes Gerados', value: metrics?.total_paletes || 0, color: 'blue', icon: <Package size={20} /> },
    { label: 'Cargas Conferidas', value: metrics?.total_cargas || 0, color: 'green', icon: <CheckCircle size={20} /> },
    { label: 'Etiquetas Conferidas', value: metrics?.total_etiquetas_conferidas || 0, color: 'cyan', icon: <Tag size={20} /> },
    { label: 'Divergências', value: metrics?.total_divergencias || 0, color: 'red', icon: <AlertTriangle size={20} /> },
    { label: 'Etiquetas Duplicadas', value: metrics?.total_duplicadas || 0, color: 'yellow', icon: <Copy size={20} /> },
    { label: 'Ocorrências', value: metrics?.total_ocorrencias || 0, color: 'purple', icon: <FileWarning size={20} /> },
  ];

  const statusData = charts?.status_conferencias || [];
  const volumeData = (charts?.volume_por_hora || []).map((v: any) => ({
    hora: `${v.hora}h`,
    total: v.total
  }));
  const divData = charts?.divergencias_tipo || [];

  return (
    <>
      {/* Indicadores Principais */}
      <div className="client-dashboard-cards">
        {cards.map((card, i) => (
          <div key={i} className={`client-dashboard-card ${card.color}`}>
            <div className="client-dashboard-card-icon">{card.icon}</div>
            <div className="client-dashboard-card-value">
              {card.value.toLocaleString('pt-BR')}
            </div>
            <div className="client-dashboard-card-label">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Gráficos */}
      <div className="client-dashboard-charts">
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Status das Conferências</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="total"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)`}
              >
                {statusData.map((_: any, i: number) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Divergências por Tipo</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={divData}
                dataKey="total"
                nameKey="tipo"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)`}
              >
                {divData.map((_: any, i: number) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="client-dashboard-chart-container" style={{ gridColumn: 'span 2' }}>
          <h3 className="client-dashboard-chart-title">Volume por Hora</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={volumeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="hora" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{
                  background: '#1e293b',
                  border: '1px solid rgba(148,163,184,0.2)',
                  borderRadius: '8px',
                  color: '#e2e8f0'
                }}
              />
              <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Etiquetas" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Motivos de Ocorrências */}
      {metrics?.motivos_ocorrencias?.length > 0 && (
        <div className="client-dashboard-table-container">
          <h3 className="client-dashboard-table-title">Motivos das Ocorrências</h3>
          <table className="client-dashboard-table">
            <thead>
              <tr>
                <th>Motivo</th>
                <th>Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {metrics.motivos_ocorrencias.map((m: any, i: number) => (
                <tr key={i}>
                  <td>{m.motivo}</td>
                  <td>{m.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Status dos Pallets em Tempo Real */}
      <div className="client-dashboard-table-container">
        <h3 className="client-dashboard-table-title">Status dos Pallets em Tempo Real</h3>
        <table className="client-dashboard-table">
          <thead>
            <tr>
              <th>Pallet</th>
              <th>Progresso</th>
              <th>Etiquetas</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pallets.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: '#64748b' }}>
                  Nenhum pallet encontrado no período
                </td>
              </tr>
            ) : (
              pallets.map((p: any, i: number) => (
                <tr key={i}>
                  <td>Pallet {String(p.numero_palete).padStart(3, '0')}</td>
                  <td>
                    <div className="client-dashboard-progress">
                      <div className="client-dashboard-progress-bar">
                        <div
                          className={`client-dashboard-progress-fill ${p.progresso >= 100 ? 'complete' : ''}`}
                          style={{ width: `${Math.min(p.progresso, 100)}%` }}
                        />
                      </div>
                      <span className="client-dashboard-progress-text">{p.progresso}%</span>
                    </div>
                  </td>
                  <td>{p.etiquetas_conferidas}/{p.etiquetas_total}</td>
                  <td>
                    <span className={`client-dashboard-badge ${
                      p.progresso >= 100 ? 'concluido' :
                      p.progresso > 0 ? 'conferindo' : 'pendente'
                    }`}>
                      {p.progresso >= 100 ? 'Finalizado' :
                       p.progresso > 0 ? 'Em Conferência' : 'Pendente'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ClientDashboardMain;
